import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
    login as apiLogin,
    register as apiRegister,
    logout as apiLogout,
    getCurrentUser,
    getCurrencyBalance,
    getAllBalances,
    TokenManager
} from '@/services/auth.js'

export const useAuthStore = defineStore('auth', () => {
    // 状态
    const user = ref(null)
    const isLoading = ref(false)
    const error = ref(null)
    const balances = ref([])
    const isLoadingBalances = ref(false)
    const balanceError = ref(null)

    // 计算属性
    const isAuthenticated = computed(() => {
        return !!user.value && !!TokenManager.getAccessToken()
    })

    const isAdmin = computed(() => {
        return user.value?.role_code === 'ADMIN'
    })

    const isOperator = computed(() => {
        return user.value?.role_code === 'OPERATOR' || isAdmin.value
    })

    const hasPermission = (permission) => {
        if (!user.value) return false

        // 管理员拥有所有权限
        if (user.value.role_code === 'ADMIN') return true

        // 根据角色判断权限
        const rolePermissions = {
            'USER': ['bet', 'view_market', 'manage_profile'],
            'OPERATOR': ['bet', 'view_market', 'manage_profile', 'manage_market', 'manage_user', 'view_admin_panel']
        }

        const userPermissions = rolePermissions[user.value.role_code] || []
        return userPermissions.includes(permission)
    }

    // 余额相关计算属性
    const gBalance = computed(() => {
        const gCurrency = balances.value.find(b => b.currency === 'G')
        if (!gCurrency) return '₲0'

        // 确保 balance 是数字类型
        const balance = typeof gCurrency.balance === 'number' ? gCurrency.balance : parseFloat(String(gCurrency.balance)) || 0
        return `₲${balance.toFixed(2)}`
    })

    const getBalanceByCurrency = (currency) => {
        const currencyBalance = balances.value.find(b => b.currency === currency)
        return currencyBalance?.balance || 0
    }

    // 初始化认证状态
    const initAuth = async () => {
        const storedUser = TokenManager.getUserInfo()
        const accessToken = TokenManager.getAccessToken()

        if (storedUser && accessToken) {
            // 检查token是否过期
            if (!TokenManager.isTokenExpired(accessToken)) {
                user.value = storedUser

                // 可选：验证token有效性
                try {
                    const currentUser = await getCurrentUser()
                    user.value = currentUser
                    TokenManager.setTokens({
                        user: currentUser,
                        access_token: accessToken,
                        refresh_token: TokenManager.getRefreshToken() || '',
                        token_type: 'Bearer',
                        expires_in: 0
                    })

                    // 初始化成功后获取余额
                    loadCurrencyBalance('G').catch(err => {
                        console.error('初始化时获取余额失败:', err)
                    })
                } catch (error) {
                    // 如果验证失败，清除无效token
                    await logout()
                }
            } else {
                // Token过期，清除本地数据
                await logout()
            }
        }
    }

    // 登录
    const login = async (credentials) => {
        isLoading.value = true
        error.value = null

        try {
            const authResponse = await apiLogin(credentials)

            // 保存认证信息
            TokenManager.setTokens(authResponse)
            user.value = authResponse.user

            // 启动token自动刷新
            const { tokenRefreshManager } = await import('@/utils/auth.js')
            tokenRefreshManager.startAutoRefresh()

            // 登录成功后立即获取余额
            loadCurrencyBalance('G').catch(err => {
                console.error('登录后获取余额失败:', err)
            })

            return authResponse
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            isLoading.value = false
        }
    }

    // 注册
    const register = async (userData) => {
        isLoading.value = true
        error.value = null

        try {
            const authResponse = await apiRegister(userData)

            // 注册成功后自动登录
            TokenManager.setTokens(authResponse)
            user.value = authResponse.user

            // 启动token自动刷新
            const { tokenRefreshManager } = await import('@/utils/auth.js')
            tokenRefreshManager.startAutoRefresh()

            // 注册成功后立即获取余额
            loadCurrencyBalance('G').catch(err => {
                console.error('注册后获取余额失败:', err)
            })

            return authResponse
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            isLoading.value = false
        }
    }

    // 登出
    const logout = async () => {
        try {
            await apiLogout()
        } finally {
            // 无论API调用是否成功，都清除本地状态
            user.value = null
            error.value = null
            balances.value = []
            TokenManager.clearTokens()

            // 停止token自动刷新
            const { tokenRefreshManager } = await import('@/utils/auth.js')
            tokenRefreshManager.stopAutoRefresh()
        }
    }

    // 更新用户信息
    const updateUser = (userData) => {
        if (user.value) {
            user.value = { ...user.value, ...userData }
            // 更新本地存储
            TokenManager.setTokens({
                user: user.value,
                access_token: TokenManager.getAccessToken() || '',
                refresh_token: TokenManager.getRefreshToken() || '',
                token_type: 'Bearer',
                expires_in: 0
            })
        }
    }

    // 清除错误
    const clearError = () => {
        error.value = null
    }

    // 检查是否需要刷新用户信息
    const refreshUserInfo = async () => {
        if (!isAuthenticated.value) return

        try {
            const currentUser = await getCurrentUser()
            user.value = currentUser

            // 更新本地存储
            TokenManager.setTokens({
                user: currentUser,
                access_token: TokenManager.getAccessToken() || '',
                refresh_token: TokenManager.getRefreshToken() || '',
                token_type: 'Bearer',
                expires_in: 0
            })
        } catch (err) {
            console.error('刷新用户信息失败:', err)
            // 如果刷新失败，可能是token无效，执行登出
            await logout()
        }
    }

    // 获取所有余额
    const loadAllBalances = async () => {
        if (!isAuthenticated.value) return

        try {
            isLoadingBalances.value = true
            balanceError.value = null

            const allBalances = await getAllBalances()
            balances.value = allBalances
        } catch (err) {
            console.error('获取余额失败:', err)
            balanceError.value = err.message || '获取余额失败'
        } finally {
            isLoadingBalances.value = false
        }
    }

    // 获取指定货币余额
    const loadCurrencyBalance = async (currency = 'G') => {
        if (!isAuthenticated.value) return

        try {
            isLoadingBalances.value = true
            balanceError.value = null

            const currencyBalance = await getCurrencyBalance(currency)

            // 更新或添加到余额列表
            const existingIndex = balances.value.findIndex(b => b.currency === currency)
            if (existingIndex >= 0) {
                balances.value[existingIndex] = currencyBalance
            } else {
                balances.value.push(currencyBalance)
            }
        } catch (err) {
            console.error(`获取${currency}余额失败:`, err)
            balanceError.value = err.message || '获取余额失败'
        } finally {
            isLoadingBalances.value = false
        }
    }

    // 清除余额错误
    const clearBalanceError = () => {
        balanceError.value = null
    }

    return {
        // 状态
        user,
        isLoading,
        error,
        balances,
        isLoadingBalances,
        balanceError,

        // 计算属性
        isAuthenticated,
        isAdmin,
        isOperator,
        gBalance,

        // 方法
        initAuth,
        login,
        register,
        logout,
        updateUser,
        clearError,
        refreshUserInfo,
        hasPermission,
        loadAllBalances,
        loadCurrencyBalance,
        getBalanceByCurrency,
        clearBalanceError
    }
})
